import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { LoaderCircle } from 'lucide-react';

import type { RiderProfile } from '@/api/types';
import { useAuth } from '@/auth/auth-context';
import { RiderSessionProvider } from '@/auth/rider-session';
import { PageFrame } from '@/components/app-shell';
import { BrandMark } from '@/components/brand-logo';
import { ErrorState } from '@/components/states';
import { TabBar } from '@/components/tab-bar';
import { Toaster } from '@/components/ui/toaster';
import { useRunSheet } from '@/hooks/use-deliveries';
import { useNewJobAlerts } from '@/hooks/use-new-job-alerts';
import { usePushBridge } from '@/hooks/use-push-alerts';
import { useRiderProfile } from '@/hooks/use-rider';
import { AccountScreen } from '@/screens/account-screen';
import { DeliveryScreen } from '@/screens/delivery-screen';
import { HistoryScreen } from '@/screens/history-screen';
import { JobsScreen } from '@/screens/jobs-screen';
import { NotARiderScreen } from '@/screens/not-a-rider-screen';
import { SignInScreen } from '@/screens/sign-in-screen';

/**
 * Four gates in a row, and each one is a different screen.
 *
 * Restoring a session, being signed out, being signed in without a rider
 * profile, and being a rider are four genuinely different states — collapsing
 * any two of them is how somebody ends up staring at an empty run sheet that
 * was never going to fill.
 */
export function App() {
  return (
    <BrowserRouter>
      {/* First stop for a keyboard, and the reason `main` carries an id. */}
      <a
        href="#main"
        className="sr-only-text focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-lg focus:bg-raised focus:px-4 focus:py-2 focus:font-semibold focus:text-ink focus:shadow-lg"
      >
        Skip to content
      </a>
      <Gate />
      <Toaster />
    </BrowserRouter>
  );
}

function Gate() {
  const { status } = useAuth();

  if (status === 'restoring') return <Splash />;

  if (status === 'signedOut') {
    // Every path lands on the sign-in screen. There is nothing here to browse
    // signed out, unlike the customer app — this is one list of jobs
    // addressed to one person, and the backend refuses all of it anonymously.
    return (
      <Routes>
        <Route path="*" element={<SignInScreen />} />
      </Routes>
    );
  }

  return <RiderGate />;
}

function RiderGate() {
  const profile = useRiderProfile();

  if (profile.isPending) return <Splash />;

  if (profile.isError) {
    return (
      <PageFrame>
        <div className="mx-auto flex w-full max-w-app flex-1 flex-col justify-center px-gutter">
          <ErrorState error={profile.error} onRetry={() => void profile.refetch()} />
        </div>
      </PageFrame>
    );
  }

  // `null` is a real answer, not a missing one — see `useRiderProfile`.
  if (!profile.data) {
    return <NotARiderScreen onRetry={() => void profile.refetch()} retrying={profile.isFetching} />;
  }

  return <RiderRoutes rider={profile.data} />;
}

function RiderRoutes({ rider }: { rider: RiderProfile }) {
  return (
    <Routes>
      <Route element={<RiderLayout rider={rider} />}>
        <Route path="/jobs" element={<JobsScreen rider={rider} />} />
        <Route path="/jobs/:deliveryId" element={<DeliveryScreen />} />
        <Route path="/history" element={<HistoryScreen />} />
        <Route path="/account" element={<AccountScreen rider={rider} />} />
        {/* The run sheet is the app. Everything else is reached from it. */}
        <Route path="*" element={<Navigate to="/jobs" replace />} />
      </Route>
    </Routes>
  );
}

/**
 * The page frame and the tab bar, rendered ONCE outside the routes.
 *
 * Switching tabs therefore does not unmount and remount the bar — one that
 * rebuilt on every navigation would lose its transition and flicker its badge,
 * which is the one number on screen a rider glances at to know whether they
 * are finished.
 */
function RiderLayout({ rider }: { rider: RiderProfile }) {
  return (
    <RiderSessionProvider rider={rider}>
      <LayoutFrame />
    </RiderSessionProvider>
  );
}

function LayoutFrame() {
  const { jobs, isSuccess } = useRunSheet();
  // Mounted here, once, so a new job is announced on whichever tab is open.
  useNewJobAlerts(jobs, isSuccess);
  usePushBridge();

  return (
    <PageFrame>
      <Outlet />
      <TabBar activeCount={jobs.length} />
    </PageFrame>
  );
}

/**
 * The moment between having a token and knowing who it belongs to.
 *
 * The mark rather than a bare spinner: this is the first thing a rider sees at
 * the start of a shift, and an unbranded spinner on an empty page is what a
 * broken app looks like.
 */
function Splash() {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-6 bg-page" role="status">
      <BrandMark size={56} />
      <LoaderCircle
        className="size-6 animate-spin text-ink-subtle motion-reduce:animate-none"
        aria-hidden="true"
      />
      <span className="sr-only-text">Signing you in</span>
    </div>
  );
}
